import { Injectable } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import parsePhoneNumber from 'libphonenumber-js';

@Injectable({ providedIn: 'root' })
export class EntityFormProvider {
  entityForm: FormGroup;

  constructor(private fb: FormBuilder) {}

  initEntityForm(data: any): void {
    this.entityForm = this.fb.group({
      roleId: [data?.id ? this.detectRoleValues(data?.entityRoles) : null, [Validators.required]],
      name: [data?.name],
      organisationEntityId: [data?.organisationEntityId],
      firstName: [data?.people.length ? data?.people[0].firstName : data?.name, [Validators.required]],
      lastName: [data?.people.length ? data?.people[0].lastName : null, [Validators.required]],
      birthDate: [data?.people.length ? (data?.people[0].dateOfBirth ? this.parseDate(data?.people[0].dateOfBirth) : null) : null],
      tradingAs: [data?.tradeName],
      previousName: [data?.entityProperties.length ? this.detectProperty(data?.entityProperties, 'previous_name') : null],
      type: [data?.type || 2, [Validators.required]],
      isPrevious: [data?.isPrevious],
      mobilePhones: this.fb.array(data?.entityProperties.length ? [] : [this.createPhoneGroup()]),
      homePhones: this.fb.array(data?.entityProperties.length ? [] : [this.createPhoneGroup()]),
      officePhones: this.fb.array(data?.entityProperties.length ? [] : [this.createPhoneGroup()]),
      addresses: data?.id && data?.addresses.length ? this.fb.array([]) : this.fb.array([this.createAddressGroup()]),
      bankDetails: data?.id && data?.entityRoles[0]?.bankAccounts.length ? this.fb.array([]) : this.fb.array([this.createBankDetailsGroup()]),
      email: this.fb.array([]),
      POFile: [data?.documents?.POFile],
      PoDFile: [data?.documents?.PoDFile],
      etrFile: [data?.documents?.etrFile],
      fxFile: [data?.documents?.fxFile],
      organisationFile: [data?.documents?.organisationFile],
      proofFile: [data?.documents?.proofFile],
      signedFile: [data?.documents?.signedFile],
      comments: [null], //@todo not in data, need to add response
    });

    if (data?.entityProperties.length) {
      const emailControlsArray = this.entityForm.get('email') as FormArray;
      const emailsArray = this.detectProperty2(data?.entityProperties, 'email');

      const mobilePhonesControlsArray = this.entityForm.get('mobilePhones') as FormArray;
      const mobilePhonesArray = this.detectProperty2(data.entityProperties, 'phone_number', 'Mobile');

      const homePhonesControlsArray = this.entityForm.get('homePhones') as FormArray;
      const homePhonesArray = this.detectProperty2(data.entityProperties, 'phone_number', 'Home');

      const officePhonesControlsArray = this.entityForm.get('officePhones') as FormArray;
      const officePhonesArray = this.detectProperty2(data.entityProperties, 'phone_number', 'Office');
      if (emailsArray.length) {
        emailsArray.forEach((email) => {
          emailControlsArray.push(this.fb.control(email, [Validators.email]));
        });
      } else {
        emailControlsArray.push(this.fb.control('', [Validators.email]));
      }

      if (mobilePhonesArray.length) {
        mobilePhonesArray.forEach((phone) => {
          const phoneNumber = phone ? this.parsePhoneNumber(phone) : null;
          mobilePhonesControlsArray.push(
            this.createPhoneGroup(phoneNumber)
          );
        });
      } else {
        mobilePhonesControlsArray.push(this.createPhoneGroup());
      }

      if (homePhonesArray.length) {
        homePhonesArray.forEach((phone) => {
          const phoneNumber = phone ? this.parsePhoneNumber(phone) : null;
          homePhonesControlsArray.push(
            this.createPhoneGroup(phoneNumber)
          );
        });
      } else {
        homePhonesControlsArray.push(this.createPhoneGroup());
      }
      if (officePhonesArray.length) {
        officePhonesArray.forEach((phone) => {
          const phoneNumber = phone ? this.parsePhoneNumber(phone) : null;
          officePhonesControlsArray.push(
            this.createPhoneGroup(phoneNumber)
          );
        });
      } else {
        officePhonesControlsArray.push(this.createPhoneGroup());
      }
    } else {
      const emailControlsArray = this.entityForm.get('email') as FormArray;
      emailControlsArray.push(this.fb.control('', [Validators.email]));
    }

    if (data?.addresses.length) {
      this.setAddressData(data.addresses);
    }

    if (data?.entityRoles[0]?.bankAccounts.length) {
      this.setBankDetailsData(data?.entityRoles[0]?.bankAccounts)
    }
  }

  private setAddressData(addresses: any) {
    addresses.forEach((address: any) => {
      (this.entityForm.get('addresses') as any).push(
        this.fb.group({
          address1: [address.lineOne, Validators.required],
          address2: [address.lineTwo],
          city: [address.city, Validators.required],
          state: [address.state],
          area: [address.area],
          postCode: [address.zipCode],
          country: [address.country, Validators.required],
          id: [address.id],
        }),
      );
    });
  }

  private setBankDetailsData(bankDetails: any) {
    bankDetails.forEach((bank: any) => {
      (this.entityForm.get('bankDetails') as any).push(
        this.fb.group({
          ccy: [bank.ccy],
          bankId: [bank.bankId],
          bankName: [bank.name],
          accountNumber: [bank.account],
          sortCode: [bank.sortCode],
          swiftBic: [bank.swiftBic],
          iban: [bank.iban],
          id: [bank.id]
        }),
      );
    });
  }

  private parseDate(param: string) {
    return {
      year: parseInt(moment(param).format('YYYY'), 10),
      day: parseInt(moment(param).format('DD'), 10),
      month: parseInt(moment(param).format('M'), 10),
    };
  }

  createPhoneGroup(phoneNumber?: any): FormGroup {
    return this.fb.group({
      countryCode: [phoneNumber ? phoneNumber[0] : null],
      areaCode: [phoneNumber ? phoneNumber[1] : null],
      mobileNumber: [phoneNumber ? phoneNumber[2] : null],
    });
  }

  createAddressGroup(): FormGroup {
    return this.fb.group({
      address1: ['', Validators.required],
      address2: [''],
      city: [null, Validators.required],
      state: [null],
      area: [''],
      postCode: [''],
      country: [null, Validators.required],
    });
  }

  createBankDetailsGroup(): FormGroup {
    return this.fb.group({
      ccy: [null],
      bankId: [null],
      bankName: [null],
      accountNumber: [null],
      sortCode: [null],
      swiftBic: [null],
      iban: [null],
    });
  }


  private parsePhoneNumber(number: string): string[] {
    const isValidNumber = parsePhoneNumber(number, 'IR')?.isValid();
    /*if (!isValidNumber && number.startsWith('00')) {
      number = number.replace(/^00/, '+');
    }*/
    if (!isValidNumber && !number.startsWith('+')) {
      number = number.startsWith('353') ? `+${number}` : `+353${number}`;
    }
    number = (number.startsWith('+') || number.startsWith('00')) ? number : `+${number}`;
    const phoneNumberParts = parsePhoneNumber(number, 'IR')?.formatInternational().split(' ');
    const containsZeroAfterPrefix = /^\+3530/.test(number);
    /*if (containsZeroAfterPrefix) {
      phoneNumberParts?.splice(1,0, '0')
    }*/
    if (phoneNumberParts && phoneNumberParts.length >= 3) {
      return [
        phoneNumberParts[0], // Country code
        phoneNumberParts[1], // Area code
        // `${phoneNumberParts[2]}-${phoneNumberParts[3]}`, // Rest of the number
        phoneNumberParts.slice(2).join('-'),
      ];
    }
    return phoneNumberParts!;
  }

  private detectProperty(data: any[], propertyId: string): string {
    const property = data.find((item) => item.propertyId === propertyId);
    return property?.propertyValue || '';
  }

  detectProperty2(data: any[], propertyId: string, propertyTitle?: string): string[] {
    const result = data
      .filter((item) => item.propertyId === propertyId && (!propertyTitle || item.propertyTitle === propertyTitle))
      .map((item) => item.propertyValue);
    return result || '';
  }

  detectRoleValues(data: any[]): string[] {
    return data.reduce((acc, curr) => {
      if (curr.role?.name) {
        acc.push(curr.role.name);
      }
      return acc
    }, [])
  }
}
